import React, { useEffect, useRef, useState } from "react"
import { Alert } from "reactstrap"
import { COUNTRIES } from "./plannerQuery"
import { addDays } from "./plannerCalendar"
import { dayLabel, todayIn } from "./plannerLabels"
import { UNCONFIRMED } from "./proposalMessages"
import { LANGUAGES, sendingMessage } from "./sendingLabels"
import AddressPicker, { addressesChanged, addressesReady, initialAddresses, recipientsOf } from "./AddressPicker"
import PlannerDialog from "./PlannerDialog"

// "Send a reminder" (ticket 16; Phase C2b): manual only, by email, of what still waits for the organizer's answer — their proposals in
// every country whose confirmed content is the version sent; one changed after it was sent waits for the updated list. The same
// address check as a sending; the email asks for an answer within 7 days. The wait is not restarted. A refusal is shown here.
export default function ReminderDialog({ organizer, list, name, country, timeZone, commands, onClose }) {
  const [language, setLanguage] = useState(organizer.lastSending?.language || list.organizer.language || "ro")
  const [address, setAddress] = useState(() => initialAddresses(list.organizer))
  const [state, setState] = useState({})
  const open = useRef(true)
  useEffect(() => () => { open.current = false }, [])
  const here = new Set(organizer.proposals.map(({ id }) => id))
  const reminded = organizer.toRemind.filter(({ id }) => here.has(id))
  const elsewhere = organizer.toRemind.filter(({ id }) => !here.has(id))
  const waitsForList = organizer.proposals.filter(({ id }) => !organizer.toRemind.some(item => item.id === id))
  const other = COUNTRIES.find(item => item !== country)
  const remind = async () => {
    setState({ pending: true })
    const result = await commands.remind({ organizerId: organizer.organizerId, language, recipients: recipientsOf(address), addressChecked: true,
      proposals: organizer.toRemind.map(({ id, revision }) => ({ id, revision })) }, { name })
    if (!open.current) return
    if (result?.status === "saved") return onClose()
    setState({ error: result?.status === "unknown" ? result.message || UNCONFIRMED : sendingMessage(result?.code, result?.details) })
  }
  return <PlannerDialog title={`Send a reminder to ${name}?`} sub="An email of the proposals still waiting for an answer, as they were sent."
    dirty={addressesChanged(address, list.organizer)} onClose={onClose}
    actions={<button type="button" className="btn btn-primary" disabled={state.pending || !list.team?.email || !addressesReady(address)} onClick={remind}>
      Send the reminder</button>}>
    <ul className="ep-stack mb-0" aria-label="What the reminder carries">
      <li>{`In ${country}: ${reminded.map(({ number }) => number).join(", ") || "none"}`}</li>
      {elsewhere.length > 0 && <li>{`In ${other}: ${elsewhere.map(({ number }) => number).join(", ")}`}</li>}
    </ul>
    {waitsForList.length > 0 && <p className="ep-meta mb-0">{`Not in the reminder: ${waitsForList.map(({ number }) => number).join(", ")}, changed after
      ${waitsForList.length === 1 ? "it was" : "they were"} sent. Send the updated list instead.`}</p>}
    <p className="ep-meta mb-0">{`The email asks for an answer by ${dayLabel(addDays(todayIn(timeZone), 7))}. The wait keeps counting from each sending.`}</p>
    {!list.team?.email && <p className="ep-banner ep-banner-warn mb-0">Emails can't be sent from this server: the calendar team's reply address is not set.</p>}
    <div className="ep-field">
      <label htmlFor="ep-remind-language">Language of the reminder</label>
      <select id="ep-remind-language" className="form-control ep-select" value={language} onChange={event => setLanguage(event.target.value)}>
        {LANGUAGES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select>
    </div>
    <AddressPicker id="ep-remind" value={address} team={list.team} onChange={setAddress} />
    {state.error && <Alert color="danger" className="mb-0">{state.error}</Alert>}
  </PlannerDialog>
}
